import { isNumber, isPositiveNumber } from '@/helpers/common.js';
import { getTimeDuration } from '@/helpers/time.js';
import { getPointsDiscountImage } from '@/helpers/image.js';
import {
  getAchievementBadgeImage,
  getLevelBadgeImage,
} from '@/helpers/reward.js';
import {
  lanternsBonusGameImage,
  puzzleBonusGameImage,
  bundleDiscountImage,
} from '@/constants/image.js';
import { rewardPrizeTypeEnum } from '@/constants/reward.js';
import {
  notificationDescriptionTextTypeEnum,
  notificationTypeEnum,
} from '@/constants/notification.js';

const getNotificationConfigurationPerType = () => ({
  [notificationTypeEnum.UPDATE_PROFILE]: {
    getTitle: () => 'page_notification_item_update_profile_title',
    getDescriptionAsList: () => [{ type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_update_profile_description' }],
    getImage: () => ({ src: '/static/images/notifications/complete_profile.svg', alt: 'Profile' }),
  },
  [notificationTypeEnum.EMAIL_VERIFICATION]: {
    getTitle: () => 'page_notification_item_email_verification_title',
    getDescriptionAsList: () => [{ type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_email_verification_description' }],
    getImage: () => ({ src: '/static/images/notifications/email.svg', alt: 'Email' }),
  },
  [notificationTypeEnum.ACHIEVEMENT]: {
    getTitle: () => 'page_notification_item_achievement_title',
    getDescriptionAsList: ({ prizeValue }) => [
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_achievement_description_pt1' },
      { type: notificationDescriptionTextTypeEnum.DYNAMIC_VALUE, value: prizeValue },
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_achievement_description_pt2' },
    ],
    getImage: ({ achievementName }) => ({ src: getAchievementBadgeImage(achievementName), alt: 'Achievement' }),
  },
  [notificationTypeEnum.LEVEL_UP]: {
    getTitle: ({ level }) => `page_notification_item_level_up_title_lvl${ level }`,
    getDescriptionAsList: ({ prizeValue }) => [
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_level_up_description_pt1' },
      { type: notificationDescriptionTextTypeEnum.DYNAMIC_VALUE, value: prizeValue },
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_level_up_description_pt2' },
    ],
    getImage: ({ level }) => ({ src: getLevelBadgeImage(level), alt: 'Level' }),
  },
  [notificationTypeEnum.BONUS_BUNDLE_DISCOUNT]: {
    getTitle: () => 'page_notification_item_bonus_bundle_discount_title',
    getDescriptionAsList: ({ duration, multiplier }) => [
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_bonus_bundle_discount_description_pt1' },
      { type: notificationDescriptionTextTypeEnum.DYNAMIC_VALUE, value: duration },
      {
        type: notificationDescriptionTextTypeEnum.STYLED_TRANSLATION_KEY,
        value: (duration === 1) ? 'page_notification_item_bonus_bundle_discount_description_pt2' : 'page_notification_item_bonus_bundle_discount_description_pt2_plural',
      },
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: `page_notification_item_bonus_bundle_discount_description_pt3_x${ multiplier }` },
    ],
    getImage: () => ({ src: bundleDiscountImage, alt: 'Bundle Discount' }),
  },
  [notificationTypeEnum.BONUS_GAME_POINTS]: {
    getTitle: () => 'page_notification_item_bonus_game_points_title',
    getDescriptionAsList: ({ duration, multiplier }) => [
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_bonus_game_points_description_pt1' },
      { type: notificationDescriptionTextTypeEnum.DYNAMIC_VALUE, value: duration },
      {
        type: notificationDescriptionTextTypeEnum.STYLED_TRANSLATION_KEY,
        value: (duration === 1) ? 'page_notification_item_bonus_game_points_description_pt2' : 'page_notification_item_bonus_game_points_description_pt2_plural',
      },
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: `page_notification_item_bonus_game_points_description_pt3_x${ multiplier }` },
    ],
    getImage: ({ multiplier }) => ({ src: getPointsDiscountImage(multiplier), alt: 'Bonus Points' }),
  },
  [notificationTypeEnum.BONUS_DAILY_VISIT]: {
    getTitle: () => 'page_notification_item_bonus_daily_visit_title',
    getDescriptionAsList: () => [{ type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_bonus_daily_visit_description' }],
    getImage: () => ({ src: '/static/images/notifications/daily_icon.svg', alt: 'Bonus Daily' }),
  },
  [notificationTypeEnum.GAME_PUZZLE]: {
    getTitle: () => 'page_notification_item_game_puzzle_title',
    getDescriptionAsList: () => [{ type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_game_puzzle_description' }],
    getImage: () => ({ src: puzzleBonusGameImage, alt: 'Puzzle Game' }),
  },
  [notificationTypeEnum.GAME_LANTERNS]: {
    getTitle: () => 'page_notification_item_game_lanterns_title',
    getDescriptionAsList: () => [{ type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_game_lanterns_description' }],
    getImage: () => ({ src: lanternsBonusGameImage, alt: 'Lanterns Game' }),
  },
  [notificationTypeEnum.REFERRAL_REGISTRATION]: {
    getTitle: () => 'page_notification_item_referral_registration_title',
    getDescriptionAsList: ({ prizeValue }) => [
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_referral_registration_description_pr1' },
      { type: notificationDescriptionTextTypeEnum.DYNAMIC_VALUE, value: prizeValue },
      { type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_referral_registration_description_pr2' },
    ],
    getImage: () => ({ src: '/static/images/notifications/new_friend.svg', alt: 'Invite Friend' }),
  },
  [notificationTypeEnum.BALANCE_ZERO_GAMES]: {
    getTitle: () => 'page_notification_item_balance_zero_games_title',
    getDescriptionAsList: () => [{ type: notificationDescriptionTextTypeEnum.TRANSLATION_KEY, value: 'page_notification_item_balance_zero_games_description' }],
    getImage: () => ({ src: '/static/images/notifications/balance.svg', alt: 'Game Balance' }),
  },
});

const getNotificationType = (notification) => {
  const notificationCategory = notification?.notificationCategoryDTO?.notifName;

  if (notificationCategory === 'CHANGE_USERNAME_OR_PIN') {
    return notificationTypeEnum.UPDATE_PROFILE;
  } else if (notificationCategory === 'EMAIL_VERIFICATION') {
    return notificationTypeEnum.EMAIL_VERIFICATION;
  } else if (notificationCategory === 'ACHIEVEMENT') {
    return notificationTypeEnum.ACHIEVEMENT;
  } else if (notificationCategory === 'LEVELED_UP') {
    return notificationTypeEnum.LEVEL_UP;
  } else if (notificationCategory === 'BUNDLE_DISCOUNT') {
    return notificationTypeEnum.BONUS_BUNDLE_DISCOUNT;
  } else if (notificationCategory === 'BONUS_POINTS_PHASE') {
    return notificationTypeEnum.BONUS_GAME_POINTS;
  } else if (notificationCategory === 'BONUS_PHASES_DAILY') {
    return notificationTypeEnum.BONUS_DAILY_VISIT;
  } else if (notificationCategory === 'PENDING_PUZZLE_GAME') {
    return notificationTypeEnum.GAME_PUZZLE;
  } else if (notificationCategory === 'PENDING_LIGHT_THE_LANTERNS_GAME') {
    return notificationTypeEnum.GAME_LANTERNS;
  } else if (notificationCategory === 'BONUS_REFERRAL_REGISTRATION') {
    return notificationTypeEnum.REFERRAL_REGISTRATION;
  } else if (notificationCategory === 'ZERO_BALANCE') {
    return notificationTypeEnum.BALANCE_ZERO_GAMES;
  } else {
    return '';
  }
};

const getNotificationUserLevelNumber = (notification) => notification.userLevel ?? 0;

const getNotificationGameId = (notification, notificationType) =>
  [notificationTypeEnum.GAME_PUZZLE, notificationTypeEnum.GAME_LANTERNS].includes(notificationType)
    ? `${ notification.gameId }`
    : '';

const getNotificationPrize = (notification, notificationType, notificationUserLevelNumber, achievementPerNotificationText, levelList) => {
  if (notificationTypeEnum.ACHIEVEMENT === notificationType) {
    return { notificationPrizeType: achievementPerNotificationText[notification.text]?.prizeType, notificationPrizeValue: achievementPerNotificationText[notification.text]?.prizeValue };
  } else if (notificationTypeEnum.LEVEL_UP === notificationType) {
    const findLevel = levelList.find(item => item.__level === notificationUserLevelNumber);

    if (findLevel) {
      return { notificationPrizeType: findLevel.prizeType, notificationPrizeValue: findLevel.prizeValue };
    }
  }

  // For Daily Bonus rewards Points or Games (bonusPoints includes both Points and Games)
  if (Object.values(rewardPrizeTypeEnum).includes(notification.gameType)) {
    return { notificationPrizeType: notification.gameType, notificationPrizeValue: notification.bonusPoints };
  }

  return (notification.bonusPoints && { notificationPrizeType: rewardPrizeTypeEnum.POINTS, notificationPrizeValue: notification.bonusPoints }) ||
    (notification.gamesEarned && { notificationPrizeType: rewardPrizeTypeEnum.GAMES, notificationPrizeValue: notification.gamesEarned }) ||
    { notificationPrizeType: null, notificationPrizeValue: null };
};

const getNotificationMultiplier = (notification, notificationType) => {
  if ([notificationTypeEnum.BONUS_BUNDLE_DISCOUNT, notificationTypeEnum.BONUS_GAME_POINTS].includes(notificationType)) {
    const multiplier = +notification.text;

    if (isNumber(multiplier)) {
      return multiplier;
    }
  }

  return null;
};

const getNotificationTitle = (notificationType, notificationUserLevelNumber, notificationConfigurationPerType) => {
  if (!Object.hasOwn(notificationConfigurationPerType, notificationType)) { return ''; }

  const { LEVEL_UP, ...restTypes } = notificationTypeEnum;

  if (notificationType === LEVEL_UP) {
    return notificationConfigurationPerType[notificationType].getTitle({ level: notificationUserLevelNumber }) || '';
  } else if (Object.values(restTypes).includes(notificationType)) {
    return notificationConfigurationPerType[notificationType].getTitle() || '';
  } else {
    return '';
  }
};

const getNotificationDescriptionAsList = (notification, notificationType, notificationPrizeValue, notificationMultiplier, notificationConfigurationPerType) => {
  if (!Object.hasOwn(notificationConfigurationPerType, notificationType)) { return []; }

  const {
    BONUS_BUNDLE_DISCOUNT,
    BONUS_GAME_POINTS,
    ACHIEVEMENT,
    LEVEL_UP,
    REFERRAL_REGISTRATION,
    ...restTypes
  } = notificationTypeEnum;

  if ([ACHIEVEMENT, LEVEL_UP, REFERRAL_REGISTRATION].includes(notificationType)) {
    return notificationConfigurationPerType[notificationType].getDescriptionAsList({ prizeValue: notificationPrizeValue });
  } else if ([BONUS_BUNDLE_DISCOUNT, BONUS_GAME_POINTS].includes(notificationType)) {
    const duration = getTimeDuration(notification.startDate, notification.endDate);

    return notificationConfigurationPerType[notificationType].getDescriptionAsList({
      duration: isPositiveNumber(duration) ? duration : 0,
      multiplier: notificationMultiplier,
    });
  } else if (Object.values(restTypes).includes(notificationType)) {
    return notificationConfigurationPerType[notificationType].getDescriptionAsList() || [];
  } else {
    return [];
  }
};

const getNotificationImage = (notification, notificationType, notificationUserLevelNumber, notificationMultiplier, notificationConfigurationPerType, achievementPerNotificationText) => {
  if (!Object.hasOwn(notificationConfigurationPerType, notificationType)) { return { notificationImageSrc: '', notificationImageAlt: '' }; }

  const {
    ACHIEVEMENT,
    LEVEL_UP,
    BONUS_GAME_POINTS,
    ...restTypes
  } = notificationTypeEnum;

  let image;

  if (ACHIEVEMENT === notificationType) {
    image = notificationConfigurationPerType[notificationType].getImage({ achievementName: achievementPerNotificationText[notification.text]?.achievementName });
  } else if (LEVEL_UP === notificationType) {
    image = notificationConfigurationPerType[notificationType].getImage({ level: notificationUserLevelNumber });
  } else if (BONUS_GAME_POINTS === notificationType) {
    image = notificationConfigurationPerType[notificationType].getImage({ multiplier: notificationMultiplier });
  } else if (Object.values(restTypes).includes(notificationType)) {
    image = notificationConfigurationPerType[notificationType].getImage();
  }

  if (image) {
    return { notificationImageSrc: image.src, notificationImageAlt: image.alt };
  } else {
    return notification.notificationCategoryDTO?.imgUrl
      ? { notificationImageSrc: notification.notificationCategoryDTO.imgUrl, notificationImageAlt: '' }
      : { notificationImageSrc: '', notificationImageAlt: '' };
  }
};

export const constructNotification = (notification, achievementPerNotificationText, levelList) => {
  const notificationConfigurationPerType = getNotificationConfigurationPerType();

  const notificationType = getNotificationType(notification);
  const notificationUserLevelNumber = getNotificationUserLevelNumber(notification);
  const notificationGameId = getNotificationGameId(notification, notificationType);
  const { notificationPrizeType, notificationPrizeValue } = getNotificationPrize(notification, notificationType, notificationUserLevelNumber, achievementPerNotificationText, levelList);
  const notificationMultiplier = getNotificationMultiplier(notification, notificationType);
  const notificationTitle = getNotificationTitle(notificationType, notificationUserLevelNumber, notificationConfigurationPerType);
  const notificationDescriptionAsList = getNotificationDescriptionAsList(notification, notificationType, notificationPrizeValue, notificationMultiplier, notificationConfigurationPerType);
  const { notificationImageSrc, notificationImageAlt } = getNotificationImage(
    notification,
    notificationType,
    notificationUserLevelNumber,
    notificationMultiplier,
    notificationConfigurationPerType,
    achievementPerNotificationText,
  );

  return {
    id: notification.id,
    startDate: notification.startDate || '',
    endDate: notification.endDate || '',
    date: notification.notificationDate || '',
    text: notification.text,
    type: notificationType,
    userLevelNumber: notificationUserLevelNumber,
    gameId: notificationGameId,
    prizeType: notificationPrizeType,
    prizeValue: notificationPrizeValue,
    multiplier: notificationMultiplier,
    title: notificationTitle,
    descriptionAsList: notificationDescriptionAsList,
    imageSrc: notificationImageSrc,
    imageAlt: notificationImageAlt,
    isUnread: (typeof notification.readStatus === 'boolean') && !notification.readStatus,
  };
};
